import { WarningCircleIcon } from '@phosphor-icons/react';
import {
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useState,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import { CopyButton } from '../CopyButton';
import { Stack } from '../Stack';
import { Heading, Paragraph } from '../Typography';

const VARIANTS = {
  success: 'success',
  error: 'error',
  default: 'default',
  info: 'info',
} as const;

// Heading/Paragraph set their gray color via unlayered SCSS, so the variant
// tone needs the ! suffix to win over them.
const TEXT_TONE: Record<keyof typeof VARIANTS, string> = {
  success: 'text-green-900!',
  error: 'text-(--error)!',
  default: 'text-gray-900!',
  info: 'text-blue-900!',
};

interface Props extends PropsWithChildren, HTMLAttributes<HTMLDivElement> {
  title: string;
  content: string;
  showIcon: boolean;
  isRounded?: boolean;
  variant: (typeof VARIANTS)[keyof typeof VARIANTS];
  showCopyButton?: boolean;
  customIcon?: ReactNode;
  spacing?: number;
}

export const Alert = ({
  title,
  content,
  children,
  showIcon = true,
  variant = VARIANTS.default,
  isRounded = true,
  className = '',
  showCopyButton = true,
  customIcon,
  spacing = 3.5,
  ...rest
}: Partial<Props>) => {
  const [copyValue, setCopyValue] = useState('');

  const alertRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        setCopyValue(node.innerText || title || '');
      }
    },
    [title],
  );

  const renderIconByVariant = () => {
    if (customIcon) {
      return customIcon;
    }

    return <WarningCircleIcon size={18} weight="fill" />;
  };

  const alertContent = (
    <div
      ref={alertRef}
      role="alert"
      className={cn(
        'p-3.5 border border-solid',
        {
          'rounded-lg': isRounded,
          // Flowbite bordered palette: border-200 + bg-50 + text-900. The
          // container's text color also feeds the icon via currentColor.
          'border-green-200 bg-green-50 text-green-900': variant === 'success',
          'border-gray-200 bg-gray-50 text-gray-900': variant === 'default',
          'border-red-200 bg-red-50 text-(--error)': variant === 'error',
          'border-blue-200 bg-blue-50 text-blue-900': variant === 'info',
        },
        className,
      )}
      {...rest}
    >
      <Stack direction="column" gap={spacing as any}>
        {title && (
          <Stack direction="row" tag="header" gap={2} align="center">
            {showIcon && renderIconByVariant()}
            <Heading level={3} className={TEXT_TONE[variant]}>
              {title}
            </Heading>
          </Stack>
        )}
        {content && (
          <Paragraph className={TEXT_TONE[variant]}>{content}</Paragraph>
        )}
        {children}
      </Stack>
    </div>
  );

  if (showCopyButton) {
    return (
      <CopyButton.Wrapper value={copyValue} variant="top-right">
        {alertContent}
      </CopyButton.Wrapper>
    );
  }

  return alertContent;
};
